import type {
  QuoteFieldDefinition,
  QuoteFieldDefinitionList,
  QuoteFieldDefinitionWriteRequest,
  QuoteFieldPoolBinding,
  QuoteFieldPoolList,
  QuoteOutputOptions,
} from '../api/active/generated/settings';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';
import {
  type FieldDisplayName,
  invalidateFieldDisplayNames,
} from '../equipment-integration/useFieldDisplayNames';
import { invalidateQuoteCatalogSession } from '../quote-integration/catalogSessionResource';

export type QuoteFieldValueType = QuoteFieldDefinition['valueType'];
export type QuoteFieldDefinitionWrite = QuoteFieldDefinitionWriteRequest;
export type QuoteFieldPool = QuoteFieldPoolList;
export type QuoteOutputColumn =
  QuoteOutputOptions['fieldCombinations'][number]['columns'][number];
export type QuoteOutputStyle = QuoteOutputOptions['styles'][number];
export type QuoteOutputFieldCombination =
  QuoteOutputOptions['fieldCombinations'][number];
export type { QuoteFieldDefinition, QuoteFieldPoolBinding, QuoteOutputOptions };

async function read<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;
  const body = (await response.json().catch(() => ({}))) as {
    detail?: string;
    title?: string;
  };
  throw new Error(
    body.detail ?? body.title ?? `请求失败（HTTP ${response.status}）`,
  );
}

function mutationHeaders(
  csrfToken: string | null,
  ifMatch?: string,
): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Idempotency-Key': randomUuid(),
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...(ifMatch ? { 'If-Match': `"${ifMatch}"` } : {}),
  };
}

export function createSettingsApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  return {
    listDisplayNames: () =>
      transport
        .requestResponse('/field-display-names', { cache: 'no-store' })
        .then(read<{ items: FieldDisplayName[] }>),
    renameDisplayName: (field: FieldDisplayName, labelZh: string) =>
      transport
        .requestResponse(
          `/field-display-names/${encodeURIComponent(field.resourceType)}/${encodeURIComponent(field.groupKey)}/${encodeURIComponent(field.fieldKey)}`,
          {
            method: 'PUT',
            headers: mutationHeaders(csrfToken, field.rowVersion),
            body: JSON.stringify({ labelZh }),
          },
        )
        .then(read<FieldDisplayName>)
        .then((updated) => {
          invalidateFieldDisplayNames();
          return updated;
        }),
    listDefinitions: () =>
      transport
        .requestResponse('/quote-fields', { cache: 'no-store' })
        .then(read<QuoteFieldDefinitionList>),
    createDefinition: (body: QuoteFieldDefinitionWrite) =>
      transport
        .requestResponse('/quote-fields', {
          method: 'POST',
          headers: mutationHeaders(csrfToken),
          body: JSON.stringify(body),
        })
        .then(read<QuoteFieldDefinition>)
        .then((definition) => {
          invalidateQuoteCatalogSession(['device-fields']);
          invalidateFieldDisplayNames();
          return definition;
        }),
    replaceDefinition: (
      definition: QuoteFieldDefinition,
      body: QuoteFieldDefinitionWrite,
    ) =>
      transport
        .requestResponse(`/quote-fields/${encodeURIComponent(definition.id)}`, {
          method: 'PUT',
          headers: mutationHeaders(csrfToken, definition.rowVersion),
          body: JSON.stringify(body),
        })
        .then(read<QuoteFieldDefinition>)
        .then((updated) => {
          invalidateQuoteCatalogSession(['device-fields']);
          invalidateFieldDisplayNames();
          return updated;
        }),
    listPool: () =>
      transport
        .requestResponse('/quote-field-pool', { cache: 'no-store' })
        .then(read<QuoteFieldPool>),
    replacePool: (pool: QuoteFieldPool, items: QuoteFieldPoolBinding[]) =>
      transport
        .requestResponse('/quote-field-pool', {
          method: 'PUT',
          headers: mutationHeaders(csrfToken, pool.aggregateResourceVersion),
          body: JSON.stringify({
            aggregateResourceVersion: pool.aggregateResourceVersion,
            bindings: items.map((item, index) => ({
              fieldKey: item.fieldKey,
              definitionId: item.definitionId,
              definitionVersion: item.definitionVersion,
              placement: item.placement,
              displayLabel: item.displayLabel,
              snapshotPolicy: 'freeze-on-select',
              enabled: item.enabled,
              defaultVisible: item.defaultVisible,
              sortOrder: index + 1,
            })),
          }),
        })
        .then(read<QuoteFieldPool>),
    getOutputOptions: () =>
      transport
        .requestResponse('/quote-output-options', { cache: 'no-store' })
        .then(read<QuoteOutputOptions>),
    replaceOutputOptions: (options: QuoteOutputOptions) =>
      transport
        .requestResponse('/quote-output-options', {
          method: 'PUT',
          headers: mutationHeaders(csrfToken, options.aggregateResourceVersion),
          body: JSON.stringify(options),
        })
        .then(read<QuoteOutputOptions>),
  };
}

export type SettingsApi = ReturnType<typeof createSettingsApi>;
